import { createContext, useContext, useLayoutEffect, useSyncExternalStore } from "react";

/** Default tab-bar wash: matches Learn and other muted chrome. */
export const PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE = "--color-muted";

/** Accordion / settings pages paint section bodies with surface (pure white in light mode). */
export const PRIMARY_NAVIGATION_STRIP_ACCORDION_SURFACE_COLOR_VARIABLE = "--color-surface";

export type PrimaryNavigationStripSurfaceColorVariable =
  | typeof PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE
  | typeof PRIMARY_NAVIGATION_STRIP_ACCORDION_SURFACE_COLOR_VARIABLE
  | "--color-background";

type HostListener = (surfaceColorVariable: PrimaryNavigationStripSurfaceColorVariable) => void;

export type PrimaryNavigationStripSurfaceRegistry = {
  getCurrent: () => PrimaryNavigationStripSurfaceColorVariable;
  publish: (surfaceColorVariable: PrimaryNavigationStripSurfaceColorVariable) => () => void;
  subscribe: (listener: HostListener) => () => void;
};

/**
 * Lets a focused accordion claim a white tab-bar fade/background so the wash matches section
 * bodies (`bg-surface`) instead of the default muted chrome. Reference-counted like opaque
 * header chrome: overlapping mount during a tab transition must not drop a still-active claim.
 *
 * When any claim requests a non-default colour, that colour wins (today only surface/white).
 * Otherwise the bar stays muted.
 */
export function createPrimaryNavigationStripSurfaceRegistry(): PrimaryNavigationStripSurfaceRegistry {
  const entries = new Map<number, PrimaryNavigationStripSurfaceColorVariable>();
  let nextId = 0;
  const listeners = new Set<HostListener>();

  const getCurrent = (): PrimaryNavigationStripSurfaceColorVariable => {
    for (const color of entries.values()) {
      if (color !== PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE) {
        return color;
      }
    }
    return PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE;
  };

  const notify = () => {
    const current = getCurrent();
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent,
    publish(surfaceColorVariable) {
      const id = ++nextId;
      entries.set(id, surfaceColorVariable);
      notify();
      return () => {
        if (entries.delete(id)) {
          notify();
        }
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const INERT_REGISTRY: PrimaryNavigationStripSurfaceRegistry = {
  getCurrent: () => PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE,
  publish: () => () => undefined,
  subscribe: () => () => undefined,
};

export const PrimaryNavigationStripSurfaceContext =
  createContext<PrimaryNavigationStripSurfaceRegistry>(INERT_REGISTRY);

/**
 * The solid bar's fill for a claimed colour: any white claim paints it `bg-surface`, otherwise it
 * keeps the muted chrome. Chrome that must join the bar seamlessly (the onboarding's tour sheet,
 * resting on it) paints the same class.
 */
export function getPrimaryNavigationStripSurfaceClassName(
  surfaceColorVariable: PrimaryNavigationStripSurfaceColorVariable,
): "bg-muted" | "bg-surface" {
  return surfaceColorVariable === PRIMARY_NAVIGATION_STRIP_DEFAULT_SURFACE_COLOR_VARIABLE
    ? "bg-muted"
    : "bg-surface";
}

/** Colour token the live bottom-tab fade and solid bar currently dissolve into. */
export function usePrimaryNavigationStripSurfaceColorVariable(): PrimaryNavigationStripSurfaceColorVariable {
  const registry = useContext(PrimaryNavigationStripSurfaceContext);
  return useSyncExternalStore(registry.subscribe, registry.getCurrent, registry.getCurrent);
}

/**
 * While `isActive` holds, claim `surfaceColorVariable` for the shared bottom-tab fade/background.
 * Callers must already gate on navigation focus when inactive tabs stay mounted.
 */
export function usePrimaryNavigationStripSurfaceColor(
  surfaceColorVariable: PrimaryNavigationStripSurfaceColorVariable,
  isActive: boolean,
): void {
  const registry = useContext(PrimaryNavigationStripSurfaceContext);

  useLayoutEffect(() => {
    if (!isActive) {
      return;
    }
    return registry.publish(surfaceColorVariable);
  }, [isActive, registry, surfaceColorVariable]);
}
